import { useState, type ReactNode } from "react";
import type { RevenueByProvider } from "@apphq/shared/types";
import { barPercent, delta, eur, hueFor, iconSrc, initialsOf, safeHttpUrl, type Tone } from "../lib/format";

export function Card({ title, aside, children, className = "" }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || aside) && (
        <div className="card-head">
          {title && <h2>{title}</h2>}
          {aside && <div className="card-aside">{aside}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Chip({ tone = "neutral", children }: { tone?: Tone["tone"]; children: ReactNode }) {
  return <span className={`chip chip-${tone}`}>{children}</span>;
}

export function Dot({ tone, title }: { tone: Tone["tone"]; title?: string }) {
  return <span className={`dot dot-${tone}`} title={title} aria-label={title} role={title ? "img" : undefined} />;
}

export function DeltaChip({ value, previous }: { value: number; previous: number | null | undefined }) {
  const d = delta(value, previous);
  if (d.tone === "none") return null;
  return <span className={`delta delta-${d.tone}`}>{d.text}</span>;
}

export function KpiCard({
  label,
  value,
  previous,
  hint,
  muted,
}: {
  label: string;
  value: ReactNode;
  /** Pass the raw numbers to show a delta chip; omit for non-numeric / unavailable KPIs. */
  previous?: { current: number; before: number | null };
  hint?: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="card kpi">
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${muted ? " kpi-muted" : ""}`}>{value}</div>
      <div className="kpi-foot">
        {previous && <DeltaChip value={previous.current} previous={previous.before} />}
        {hint && <span className="kpi-hint">{hint}</span>}
      </div>
    </div>
  );
}

export function LoadingState({ label = "Laden…" }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <div className="spinner spinner-dark" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ title, children, icon = "○" }: { title: string; children?: ReactNode; icon?: string }) {
  return (
    <div className="state state-empty">
      <div className="state-icon" aria-hidden>
        {icon}
      </div>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state state-error" role="alert">
      <strong>Dit kon niet worden geladen</strong>
      <p>{message}</p>
      {onRetry && (
        <button className="pill" onClick={onRetry}>
          Opnieuw proberen
        </button>
      )}
    </div>
  );
}

/** `seed` (the stable app id) drives the placeholder colour so a rename only changes the letter. */
export function AppIcon({ name, url, size = 36, seed }: { name: string; url?: string | null; size?: number; seed?: string }) {
  // an external icon can be blocked (CSP) or broken: remember the failing URL and fall back to initials
  const [failed, setFailed] = useState<string | null>(null);
  const src = iconSrc(url, failed);
  const style = { width: size, height: size, borderRadius: size * 0.225, fontSize: size * 0.38 };
  if (src) return <img className="app-icon" src={src} alt="" style={style} referrerPolicy="no-referrer" loading="lazy" onError={() => setFailed(src)} />;
  const h = hueFor(seed ?? name);
  return (
    <span className="app-icon app-icon-ph" style={{ ...style, background: `linear-gradient(145deg, hsl(${h} 80% 62%), hsl(${(h + 40) % 360} 75% 48%))` }} aria-hidden>
      {initialsOf(name)}
    </span>
  );
}

/**
 * Command-center button: only when a valid URL is set; otherwise nothing (or "Niet ingesteld").
 * With `appId` the link goes through the session-gated AppHQ endpoint, which mints a short-lived
 * single-use SSO token server-side and redirects (so you land logged in). Without `appId` (the
 * unsaved Settings preview) it is the plain URL.
 */
export function CommandCenterButton({ url, appId, showUnset = false }: { url: string | null | undefined; appId?: string; showUnset?: boolean }) {
  const safe = safeHttpUrl(url);
  if (!safe) return showUnset ? <span className="muted">Niet ingesteld</span> : <span className="muted">—</span>;
  const href = appId ? `/api/admin/apps/${encodeURIComponent(appId)}/cc-open` : safe;
  return (
    <a className="pill pill-primary" href={href} target="_blank" rel="noopener noreferrer">
      Openen ↗
    </a>
  );
}

export function BarList({
  rows,
  format = eur,
}: {
  rows: Array<{ key: string; label: ReactNode; value: number }>;
  format?: (n: number) => string;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0);
  return (
    <ul className="bar-list">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="bar-row">
            <span className="bar-label">{r.label}</span>
            <span className="bar-value">{format(r.value)}</span>
          </div>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${barPercent(r.value, max)}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function Sparkline({ values, width = 92, height = 28 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) return <span className="muted">—</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => `${((i / (values.length - 1)) * (width - 2) + 1).toFixed(1)},${(height - 2 - ((v - min) / span) * (height - 4)).toFixed(1)}`);
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <polyline points={pts.join(" ")} fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function TableWrap({ children }: { children: ReactNode }) {
  return <div className="table-wrap">{children}</div>;
}

const PROVIDER_LABEL = { apple: "App Store", stripe: "Stripe" } as const;

/** Period revenue split Apple vs Stripe (`revenue_by_provider`). Only meaningful with revenue from both sources, else a hint. */
export function ProviderSplitCard({ split, aside }: { split: RevenueByProvider; aside?: ReactNode }) {
  const rows = (Object.keys(PROVIDER_LABEL) as Array<keyof typeof PROVIDER_LABEL>).map((k) => ({
    key: k,
    label: `${PROVIDER_LABEL[k]} · ${split[k].transactions}×`,
    value: split[k].revenue,
  }));
  const empty = rows.every((r) => r.value === 0 && split[r.key].transactions === 0);
  return (
    <Card title="Omzet per bron" aside={aside}>
      {empty ? (
        <EmptyState title="Nog geen omzet in deze periode">App Store en Stripe (abonnementen en commissie) verschijnen hier zodra er omzet is.</EmptyState>
      ) : (
        <BarList rows={rows} />
      )}
    </Card>
  );
}
